import { Component, OnInit, Input, Output, ViewChild, EventEmitter, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DialogService } from 'primeng/dynamicdialog';

import { config, inspectionItemColorIndex, workStateIconSolid, workStateTitle } from 'app/constants';
import { templateTypes } from 'app/constants/templates/template-type';

import { HTTPService, HelperService } from 'app/services';
import { SendDialogComponent } from 'app/modules/app-components';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { findIndex } from 'lodash';

@Component({
  selector: 'app-inspection-card',
  templateUrl: './inspection-card.component.html',
  styleUrls: ['./inspection-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InspectionCardComponent implements OnInit {
  @Input() cardIndex;
  @Input() technicians: any;
  @Input() customer: any = {};
  @Input() inspection: any;
  @Input() service: any;
  @Input() vehicle: any;
  @Input() estimate: any;
  @Input() orderTitle: string = '';
  @Input() estimateId: string = '';
  @Input() isDvi2Enabled: boolean = false;
  @Input() techInitials: any = {};

  @Output() deleteInspection = new EventEmitter<any>();
  @Output() updateInspection = new EventEmitter<any>();
  @Output() appOpenAssignTechDialog: EventEmitter<any> = new EventEmitter();
  @Output() openInspectionDialog = new EventEmitter();

  @ViewChild('fileInput', { static: true }) fileInput: any;

  inspectionItemCount: number = 0;
  categoryCount: number = 0;
  selectedTechnician: any;
  colors = config.inspectionStateColors;
  inspectionActions = [{ name: 'Send Inspection', value: templateTypes.inspection }];
  notes: string = null;
  inspectionStatus: string = 'new';
  workStateIconSolid: any = {};
  workStateTitle: any = {};
  serviceActions = [
    { name: 'View as Technician', value: 'viewAsTech', icon: 'fal fa-eye font-icon-warning', color: '' },
    { name: 'Delete', value: 'delete', icon: 'fas fa-trash font-icon-red' , color: 'font-icon-red' },
  ];
  defaultCount = {
    categories: 0,
    items: 0,
    condition: {
      good: 0,
      normal: 0,
      bad: 0,
      notInspected: 0,
    },
    images: 0,
    audios: 0,
    videos: 0,
  };
  counts = this.helperService.deepCopy(this.defaultCount);

  constructor(private httpService: HTTPService, public helperService: HelperService, private dialogService: DialogService, private activatedRoute: ActivatedRoute, private router: Router) { }

  ngOnInit() {
    this.populateInspection();
    if (this.isDvi2Enabled) {
      this.setInspectionCounts(this.inspection.inspectionId);
      if(this.inspection.inspectionId && this.inspection.inspectionId?.associatedInspectionId && this.inspection.inspectionId?.isTireStorageInspection) {
        const index = findIndex(this.serviceActions, { value: 'delete' });
        if(index > -1) {
          this.serviceActions.splice(index, 1);
        }
        
      }
      this.workStateTitle = workStateTitle;
      this.workStateIconSolid = workStateIconSolid;
    }
  }

  setInspectionCounts(inspection: any) {
    if (!inspection) {
      return;
    }

    if (inspection.category && inspection.category.length) {
      this.counts.categories = inspection.category.length;
      inspection?.category?.forEach((category: any) => {
        if (category?.inspectionItems?.length) {
          this.counts.items += category?.inspectionItems?.length ?? 0;
          category.inspectionItems.forEach(
            ({ attachments, colorIndex }: any) => {
              if (colorIndex === -1) {
                this.counts.condition.notInspected += 1;
              } else {
                this.counts.condition[
                  inspectionItemColorIndex[colorIndex]
                ] += 1;
              }

              if (attachments && attachments.length) {
                attachments.forEach(({ type }) => {
                  if (type === 'image') {
                    this.counts.images += 1;
                  } else if (type === 'voice') {
                    this.counts.audios += 1;
                  } else if (type === 'video') {
                    this.counts.videos += 1;
                  }
                });
              }
            }
          );
        }
      });
    }

    // this.categoryItemText = `
    // ${this.counts.categories} ${this.counts.categories > 1 ? 'categories' : 'category'
    //   }
    // ${this.counts.items} ${this.counts.items > 1 ? 'items' : 'item'}
    // `;
  }

  /** Populate inspection */
  populateInspection() {
    if (this.inspection.category && this.inspection.category.length > 0) {
      this.inspection.category.forEach((category) => {
        this.categoryCount += 1;
        this.inspectionItemCount += (category && category.inspectionItems && category.inspectionItems.length) || 0;
      });
    }

    if (this.inspection.statuses && this.inspection.statuses.length) {
      const statuses = this.inspection.statuses;
      if (statuses.length === 1) {
        this.inspectionStatus = statuses[0];
      } else if (statuses.indexOf('inprogress') > -1) {
        this.inspectionStatus = 'inprogress';
      }
    }
  }

  /** Remove item and also category or inspection if they don't have items and categories respectively */
  removeInspectionItemConfirm(categoryIndex, inspectionItemIndex) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Delete ${this.inspection.category[categoryIndex].inspectionItems[inspectionItemIndex].title}`,
        message: 'Are you sure you want to delete this inspection item?',
        button: {
          success: { title: 'Delete' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this._removeInspectionItem(categoryIndex, inspectionItemIndex);
      }
      onClose.unsubscribe();
    });
  }

  _removeInspectionItem(categoryIndex: number, inspectionItemIndex: number) {
    this.inspection.category[categoryIndex].inspectionItems.splice(inspectionItemIndex, 1);

    if (!this.inspection.category[categoryIndex].inspectionItems.length) {
      this.inspection.category.splice(categoryIndex, 1);
    }

    if (!this.inspection.category.length) {
      this.deleteCompleteInspection();
    }
    this._updateEstimateInspection(false);
  }

  /** Update tech for inspection and then make call to save it in the db */
  selectTechnicianForInspection(technician: any) {
    if (this.inspectionStatus === 'inprogress') {
      this.helperService.showToaster('error', 'You can not update inprogress inspection');
      return;
    }
    const tech = {
      techId: technician._id,
      name: `${technician.firstName || ''} ${technician.lastName || ''}`,
      associatedAt: new Date(),
      isAssigned: true,
    };
    this.inspection.category.forEach((cat) => {
      if (!cat.tech || (this.inspection.tech && this.inspection.tech.techId) === cat.tech.techId) {
        cat.tech = tech;
      }
    });
    this.inspection.tech = tech;
    this._assignTechToInspection(this.inspection._id, this.inspection, false);
  }

  /** renders initials of the technician name */
  renderInitials({ firstName, lastName }) {
    return this.helperService.fetchInitials(`${firstName} ${lastName}`);
  }

  /** delete coomplete inspection from the local and from the server */
  deleteCompleteInspectionConfirm() {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Delete ${this.inspection.title}`,
        message: 'Are you sure you want to delete this inspection?',
        button: {
          success: { title: 'Delete' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.deleteCompleteInspection();
      }
      onClose.unsubscribe();
    });
  }

  deleteCompleteInspection() {
    const inspectionId = this.inspection._id;
    this._deleteInspection(inspectionId);

    this.deleteInspection.emit({ inspectionIndex: this.cardIndex });
  }

  selectTechnicianForCategory(technician, categoryIndex) {
    this.inspection.category[categoryIndex].tech = {
      techId: technician._id,
      name: `${technician.firstName || ''} ${technician.lastName || ''}`,
      associatedAt: new Date(),
      isAssigned: true,
    };
    this._assignTechToInspection(this.inspection._id, this.inspection, false);
  }

  selectColorState(colorIndex: number, itemIndex: number, categoryIndex: number) {
    if (this.inspectionStatus === 'inprogress') {
      return;
    }
    this.inspection.category[categoryIndex].inspectionItems[itemIndex].colorIndex = colorIndex;
    this._updateEstimateInspection(false);
  }

  handleInspectionAction(event: any) {
    if (this.customer.customerId) {
      const company = this.helperService.getCompany();
      const ref = this.dialogService.open(SendDialogComponent, {
        width: '45%',
        styleClass: 'add-dialog send-dialog',
        showHeader: false,
        data: {
          data: this.estimate,
          title: 'Inspection',
          templateType: templateTypes.inspection,
          customer: this.customer,
          company: { name: company.companyName, logo: '' },
          vehicle: this.vehicle,
          inspectionId: this.inspection._id,
          inspectionTime: this.inspection.dateTime,
        },
      });

      const onClose = ref.onClose.subscribe((close: any) => {
        if (close && close._id) {
          const id = this.activatedRoute.snapshot.paramMap.get('id');
          if (!id) {
            this.router.navigate(['../estimate', close._id]);
          }
        }
        onClose.unsubscribe();
      });
    } else {
      this.helperService.showToaster('error', 'Please select a customer first!');
    }
  }

  saveAttachment(close) {
    this._updateEstimateInspection(false);
    this.updateInspection.emit({ inspectionIndex: this.cardIndex });
    if (close) {
      close();
    }
  }

  _deleteInspectionItem(categoryIndex: number, inspectionItemIndex: number) {
    const category = this.inspection.category[categoryIndex];
    const inspectionItem = category.inspectionItems[inspectionItemIndex];
    this.httpService.deleteInspectionItem(this.inspection._id, category._id, inspectionItem._id).then(
      (res) => {
        this._removeInspectionItem(categoryIndex, inspectionItemIndex);
        this.helperService.showToaster('success', 'Successfully Deleted');
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  _deleteInspection(inspectionId: string) {
    this.httpService.deleteEstimateInspection(inspectionId, this.estimateId, this.inspection.title).then(
      (res) => {
        this.helperService.showToaster('success', 'Successfully Deleted');
      },
      (err) => {
        this.helperService.showToaster('err', err);
      }
    );
  }

  _updateEstimateInspection(showToaster = true) {
    const inspection = { ...this.inspection };
    const inspectionId = inspection._id;
    delete inspection._id;
    const technicianIds = [];
    const techs = [];

    for (let i = 0; i < inspection.category.length; i++) {
      const category = inspection.category[i];
      if (category.tech && category.tech.techId) {
        technicianIds.push(category.tech);
      }
    }

    for (let i = 0; i < inspection.category.length; i++) {
      const category = inspection.category[i];
      if (category.tech && category.tech.techId && technicianIds.indexOf(category.tech.techId) === -1) {
        techs.push(category.tech);
      }
    }
    inspection.techs = techs;
    this.httpService.updateEstimateInspection(inspectionId, inspection).then(
      (res) => {
        if (showToaster) {
          this.helperService.showToaster('success', 'Successfully Updated');
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  _updateInspectionItem(categoryIndex, inspectionItemIndex) {
    const category = this.inspection.category[categoryIndex];
    const inspectionItem = { ...category.inspectionItems[inspectionItemIndex] };
    const inspectionItemId = inspectionItem._id;

    delete inspectionItem._id;
    delete inspectionItem._id;
    const data = { ...category, categoryId: category._id };
    this.httpService.updateInspectionItem(inspectionItemId, data).then(
      (res) => {
        this.helperService.showToaster('success', 'Successfully Updated');
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  _assignTechToInspection(inspectionId: string, inspection: any, showToaster: boolean = true) {
    this.httpService.assignTechToInspection(inspectionId, inspection).then(
      (res) => {
        if (showToaster) {
          this.helperService.showToaster('success', 'Successfully Updated');
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  renderStatus() {
    const statuses = (this.inspection && this.inspection.statuses) || [];
    if (statuses && statuses.length) {
      if (statuses.length === 1) {
        return this.helperService.serviceInspectionStatusMap(statuses[0]);
      }
      return this.helperService.serviceInspectionStatusMap('inprogress');
    }

    return this.helperService.serviceInspectionStatusMap('new');
  }

  addNewInspection() {

  }

  handleOpenTechDialog() {
    if (this.estimate.isArchived) return;
    this.appOpenAssignTechDialog.emit(this.service._id);
  }

  handleServiceAction(action: any) {
    if (action.value === 'delete') {
      this.handleConfirmInspectionDelete(
        {
          title: this.service?.inspection?.title,
          inspectionId: this.inspection.inspectionId._id,
        }
      );
    } else if (action.value === 'viewAsTech') {
      this.navigateToTech();
    }
  }

  handleOpenInspection() {
    this.openInspectionDialog.emit({
      inspectionId: this.inspection.inspectionId._id,
      serviceId: this.service._id,
      isAlCertified: this.inspection.inspectionId.isAlCertified,
    });
  }

  navigateToTech() {
    if (this.service.tech && this.service.tech.techId) {
      const { emplyeeId } = this.helperService.getCompany();
      if (this.service.tech.techId === emplyeeId || this.service.hTechOne.techId === emplyeeId || this.service.hTechTwo.techId === emplyeeId) {
        this.helperService.openPageInSameTab(`/tech/service/${this.estimateId}/${this.service._id}`, 'set');
      } else {
        this.helperService.showToaster('error', 'You are not assigned to any service in the RO');
        this.handleOpenTechDialog();
      }
    } else {
      this.helperService.showToaster('error', 'You are not assigned to any service in the RO');
      this.handleOpenTechDialog();
    }
  }

  handleConfirmInspectionDelete({ title, inspectionId }) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '60%',
      styleClass: 'small-heading-dialog parts-ordering',
      showHeader: false,
      data: {
        inspectionId,
        isCustomizedConfirmationBox: true,
        headerIconType: 'header-icon',
        isHTML: true,
        type: 'Confirm',
        styleClassHeading: 'font-18 font-700',
        styleClassCloseIcon: 'font-24 font-400 font-gray',
        headerIcon: 'fal fa-exclamation-triangle font-icon-red',
        title: 'Delete Inspection',
        message: `
        <span class="font-14 font-400" style="color:#252828">
        Are you sure you want to delete the Inspection from this repair order?
        </span>`,
        button: {
          success: {
            title: 'Delete',
            backgroundColor: '#D47877',
          },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess && data.inspectionId) {
        this.deleteInspection.emit({
          inspectionId: data.inspectionId,
          serviceId: this.service._id,
        });
      }
      onClose.unsubscribe();
    });
  }

  renderCategoriesAndItems(inspection: any) {
    let categoryCount = 0;
    let itemCount = 0;

    inspection?.category?.forEach((category: any) => {
      categoryCount += 1;
      if (category?.inspectionItems && category?.inspectionItems?.length) {
        itemCount += category.inspectionItems.length;
      }
    });

    return `${categoryCount} ${categoryCount > 1 ? 'categories' : 'category'
      }, ${itemCount} ${itemCount > 1 ? 'items' : 'item'}`;
  }

  statusMapper(status: string) {
    const labels = {
      todo: 'To do',
      inProgress: 'In Progress',
      completed: 'Completed',
    }

    return labels[status] || status;
  }

  toggleTechCard(techId, cardId) {}
}
